import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Lead, LeadState, LeadCycle, LeadDisqualificationReason } from '../models/lead.model';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { DirectoryUser } from '../../../shared/services/user-directory-api.service';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { QuickActionsMenuComponent } from '../../../shared/components/quick-actions-menu/quick-actions-menu.component';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import { CompanyResponse } from '../../empresas/models/empresa.model';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { TaskDetailModalComponent } from '../../tasks/task-detail-modal/task-detail-modal.component';
import { TicketsDetailModalComponent } from '../../tickets/tickets-detail-modal/tickets-detail-modal.component';
import { TicketsApiService } from '../../tickets/services/tickets-api.service';
import { TicketsConfigApiService } from '../../tickets/services/tickets-config-api.service';
import { Ticket } from '../../tickets/models/tickets.model';
import {
  TicketCategory,
  TicketPipeline,
  TicketPriority,
  TicketStatus
} from '../../tickets/models/tickets-config.model';

@Component({
  selector: 'app-lead-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    BadgeComponent,
    QuickActionsMenuComponent,
    AuditTimelineComponent,
    TaskDetailModalComponent,
    TicketsDetailModalComponent
  ],
  templateUrl: './lead-detail-modal.component.html'
})
export class LeadDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() lead: Lead | null = null;
  @Input() leadStates: LeadState[] = [];
  @Input() leadCycles: LeadCycle[] = [];
  @Input() disqualificationReasons: LeadDisqualificationReason[] = [];
  @Output() close = new EventEmitter<void>();

  contact: Contact | null = null;
  loadingContact = false;

  company: CompanyResponse | null = null;
  loadingCompany = false;

  registeredByUser: any = null;
  ownerUser: any = null;
  loadingUsers = false;

  // Audit timeline
  activeTab: 'info' | 'timeline' = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  // Inc 25: entidades del timeline enlazables a su detalle (tarea/ticket).
  readonly timelineLinkableEntities = ['task', 'ticket'];

  // Inc 25: detalle de tarea embebido (se abre desde el enlace del timeline).
  showTaskDetailModal = false;
  detailTaskId?: number;

  // Inc 25: detalle de ticket embebido (se abre desde el enlace del timeline).
  showTicketDetailModal = false;
  selectedTicket: Ticket | null = null;
  loadingTicket = false;
  ticketPriorities: TicketPriority[] = [];
  ticketStatuses: TicketStatus[] = [];
  ticketCategories: TicketCategory[] = [];
  ticketPipelines: TicketPipeline[] = [];
  private ticketCatalogsLoaded = false;

  // Inc 30: secciones colapsables del detalle (acordeón).
  expandedSections: Record<string, boolean> = {
    general: true,
    contactoEmpresa: true,
    descalificacion: true,
    actividad: false,
    registro: false,
  };

  constructor(
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService,
    private ticketsApi: TicketsApiService,
    private ticketsConfigApi: TicketsConfigApiService
  ) {}

  // ── Inc 30: acordeón ──
  toggleSection(key: string): void {
    this.expandedSections[key] = !this.expandedSections[key];
  }

  isSectionOpen(key: string): boolean {
    return !!this.expandedSections[key];
  }

  // ── Inc 25: abrir el detalle de la tarea/ticket creado desde este lead ──
  // Ambos abren su modal de detalle ENCIMA de este modal, sin perder el contexto
  // del lead (al cerrar se vuelve al timeline). No se navega fuera del lead.
  openTimelineEntity(evt: { entityName: string; idEntity: string }): void {
    const id = Number(evt.idEntity);
    if (!id || isNaN(id)) return;

    if (evt.entityName === 'task') {
      this.detailTaskId = id;
      this.showTaskDetailModal = true;
      return;
    }

    if (evt.entityName === 'ticket') {
      this.openTicketDetail(id);
    }
  }

  closeTaskDetail(): void {
    this.showTaskDetailModal = false;
    this.detailTaskId = undefined;
  }

  // El modal de detalle de ticket necesita el objeto completo + catálogos.
  // Cargamos los catálogos una sola vez (degradan a [] si fallan) y luego el
  // ticket por id; solo entonces se abre el modal.
  private openTicketDetail(id: number): void {
    this.loadingTicket = true;
    this.ensureTicketCatalogs().subscribe(() => {
      this.ticketsApi.getTicketById(id).subscribe({
        next: (ticket) => {
          this.selectedTicket = ticket;
          this.showTicketDetailModal = true;
          this.loadingTicket = false;
        },
        error: () => { this.loadingTicket = false; }
      });
    });
  }

  private ensureTicketCatalogs() {
    if (this.ticketCatalogsLoaded) return of(true);
    return forkJoin({
      priorities: this.ticketsConfigApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[]))),
      statuses: this.ticketsConfigApi.getStatuses().pipe(catchError(() => of([] as TicketStatus[]))),
      categories: this.ticketsConfigApi.getCategories().pipe(catchError(() => of([] as TicketCategory[]))),
      pipelines: this.ticketsConfigApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[])))
    }).pipe(
      catchError(() => of({ priorities: [], statuses: [], categories: [], pipelines: [] })),
      map((c: { priorities: TicketPriority[]; statuses: TicketStatus[]; categories: TicketCategory[]; pipelines: TicketPipeline[] }) => {
        this.ticketPriorities = c.priorities;
        this.ticketStatuses = c.statuses;
        this.ticketCategories = c.categories;
        this.ticketPipelines = c.pipelines;
        this.ticketCatalogsLoaded = true;
        return true;
      })
    );
  }

  closeTicketDetail(): void {
    this.showTicketDetailModal = false;
    this.selectedTicket = null;
  }

  // ── Inc 30: prioridad legible (tolerante a inglés/español) ──
  priorityColor(priority?: string | null): 'error' | 'warning' | 'success' | 'info' | 'light' {
    const p = (priority || '').toLowerCase();
    if (p.includes('urgent') || p.includes('urgente') || p.includes('critical') || p.includes('crítica') || p.includes('critica')) return 'error';
    if (p.includes('high') || p.includes('alta')) return 'error';
    if (p.includes('med') || p.includes('normal')) return 'warning';
    if (p.includes('low') || p.includes('baja')) return 'success';
    return 'light';
  }

  priorityLabel(priority?: string | null): string {
    const p = (priority || '').toLowerCase();
    if (p.includes('urgent') || p.includes('urgente')) return 'Urgente';
    if (p.includes('critical') || p.includes('crítica') || p.includes('critica')) return 'Crítica';
    if (p.includes('high') || p.includes('alta')) return 'Alta';
    if (p.includes('med') || p.includes('normal')) return 'Media';
    if (p.includes('low') || p.includes('baja')) return 'Baja';
    return priority || '—';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['lead']) {
      if (this.isOpen && this.lead?.id) {
        this.cargarUsuarios();
        if (this.lead.idContact) this.cargarContacto(this.lead.idContact);
        if (this.lead.idCompany) this.cargarEmpresa(this.lead.idCompany);
      } else if (!this.isOpen) {
        this.contact = null;
        this.loadingContact = false;
        this.company = null;
        this.loadingCompany = false;
        this.registeredByUser = null;
        this.ownerUser = null;
        this.loadingUsers = false;
        this.activeTab = 'info';
        this.timelineLogs = [];
        this.timelineError = '';
        this.timelinePage = 0;
      }
    }
  }

  private cargarContacto(idContact: number): void {
    this.loadingContact = true;
    this.contactoApi.getContactById(idContact).subscribe({
      next: (c) => { this.contact = c; this.loadingContact = false; },
      error: () => { this.loadingContact = false; }
    });
  }

  private cargarEmpresa(idCompany: number): void {
    this.loadingCompany = true;
    this.empresaApi.getCompanyById(idCompany).subscribe({
      next: (c) => { this.company = c; this.loadingCompany = false; },
      error: () => { this.loadingCompany = false; }
    });
  }

  private cargarUsuarios(): void {
    const registerId = this.lead?.userRegisterId;
    const ownerId = this.lead?.userPropietyId;

    if (!registerId && !ownerId) return;

    this.loadingUsers = true;
    this.registeredByUser = null;
    this.ownerUser = null;

    const currentUser = this.authService.getCurrentUser();
    // El usuario en sesión ya está en memoria: se evita la llamada. Se normaliza al shape del
    // directorio (`mail`, no `email`) para que ambas ramas devuelvan exactamente lo mismo.
    const sessionUser: DirectoryUser | null = currentUser
      ? {
          id: currentUser.id,
          fullname: currentUser.fullname,
          lastname: currentUser.lastname,
          mail: currentUser.email,
        }
      : null;
    const resolve = (id: number | undefined): Observable<DirectoryUser | null> =>
      id && id === currentUser?.id
        ? of(sessionUser)
        : id
          ? this.authApiService.getUserById(id)
          : of(null);

    if (registerId === ownerId) {
      resolve(registerId).subscribe({
        next: (user) => {
          this.registeredByUser = user;
          this.ownerUser = user;
          this.loadingUsers = false;
        },
        error: () => { this.loadingUsers = false; }
      });
    } else {
      forkJoin([resolve(registerId), resolve(ownerId)]).subscribe({
        next: ([registerUser, ownerUser]) => {
          this.registeredByUser = registerUser;
          this.ownerUser = ownerUser;
          this.loadingUsers = false;
        },
        error: () => { this.loadingUsers = false; }
      });
    }
  }

  get initials(): string {
    if (!this.lead?.name) return '?';
    const words = this.lead.name.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }

  get avatarColorClass(): string {
    if (!this.lead?.name) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400',
    ];
    const index = this.lead.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  }

  getStateName(): string {
    if (!this.lead?.idLeadState) return 'Sin tipo';
    return this.lead.leadStateName ||
      this.leadStates.find(s => s.id === this.lead?.idLeadState)?.name ||
      'Sin tipo';
  }

  getCycleName(): string {
    if (!this.lead?.idLeadCycle) return 'Sin ciclo';
    return this.lead.leadCycleName ||
      this.leadCycles.find(c => c.id === this.lead?.idLeadCycle)?.name ||
      'Sin ciclo';
  }

  getDisqualificationReasonName(): string {
    if (!this.lead?.idDisqualificationReason) return '—';
    return this.disqualificationReasons.find(r => r.id === this.lead?.idDisqualificationReason)?.name || '—';
  }

  get contactFullName(): string {
    if (!this.contact) return '—';
    return [this.contact.fullname, this.contact.lastname].filter(Boolean).join(' ');
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  formatDateShort(dateString?: string): string {
    return this.datetimeService.formatDateShort(dateString);
  }

  formatDateTime(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  // ── Timeline ──

  onTabChange(tab: 'info' | 'timeline'): void {
    this.activeTab = tab;
    if (tab === 'timeline' && this.timelineLogs.length === 0 && !this.loadingTimeline) {
      this.loadTimeline(0);
    }
  }

  loadTimeline(page = 0): void {
    if (!this.lead?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    // Inc 10: usar el timeline ENRIQUECIDO (findEntityTimeline) que resuelve
    // nombres, acciones en español y solo campos modificados, en vez del
    // listado plano de /logs.
    this.auditApi.getEntityTimeline('lead', String(this.lead.id), { page, size: 15 }).subscribe({
      next: data => {
        this.timelineLogs = data.content ?? [];
        this.timelinePage = data.page ?? page;
        this.timelineTotalPages = data.totalPages ?? 1;
        this.timelineTotalElements = data.totalElements ?? 0;
        this.loadingTimeline = false;
      },
      error: () => {
        this.timelineError = 'Error al cargar la línea de tiempo.';
        this.loadingTimeline = false;
      }
    });
  }

  // La presentación de los eventos del timeline (traducciones, diffs, títulos)
  // y el expand/colapso por log viven en AuditTimelineComponent / AuditDisplayService.

 translateCycleName(cycleName: string): string {
   if (!cycleName) return 'Sin ciclo';
   const cycleMap: Record<string, string> = {
     'New': 'Nuevo',
     'Contacted': 'Contactado',
     'Qualified': 'Calificado',
     'Proposal': 'Propuesta',
     'Negotiation': 'Negociación',
     'Closed Won': 'Cerrado Ganado',
     'Closed Lost': 'Cerrado Perdido',
     'new': 'Nuevo',
     'contacted': 'Contactado',
     'qualified': 'Calificado',
     'proposal': 'Propuesta',
     'negotiation': 'Negociación',
     'closed won': 'Cerrado Ganado',
     'closed lost': 'Cerrado Perdido',
   };
   return cycleMap[cycleName] || cycleName;
 }

 translateStateName(stateName: string): string {
   if (!stateName) return 'Sin Tipo';
   const stateMap: Record<string, string> = {
     'Active': 'Activo',
     'Inactive': 'Inactivo',
     'Disqualified': 'Descalificado',
     'active': 'Activo',
     'inactive': 'Inactivo',
     'disqualified': 'Descalificado',
   };
   return stateMap[stateName] || stateName;
 }

  translateDisqualificationReason(reason: string): string {
    if (!reason || reason === '—') return reason;
    const reasonMap: Record<string, string> = {
      'Not interested': 'No interesado',
      'Budget issues': 'Problemas de presupuesto',
      'Bad timing': 'Mal timing',
      'No authority': 'Sin autoridad',
      'Competitor': 'Competidor',
      'Not a priority': 'No es prioridad',
      'Other': 'Otro',
      'not interested': 'No interesado',
      'budget issues': 'Problemas de presupuesto',
      'bad timing': 'Mal timing',
      'no authority': 'Sin autoridad',
      'competitor': 'Competidor',
      'not a priority': 'No es prioridad',
      'other': 'Otro',
    };
    return reasonMap[reason] || reason;
  }

  onClose(): void {
    this.close.emit();
  }
}
